/*                                 */
/*                                                                      */
import {
	BLOCK_EFFECT,
	BRANCH_EFFECT,
	CLEAN,
	DESTROYED,
	DIRTY,
	EFFECT,
	ASYNC,
	INERT,
	RENDER_EFFECT,
	ROOT_EFFECT,
	MAYBE_DIRTY,
	DERIVED,
	EAGER_EFFECT,
	ERROR_VALUE,
	MANAGED_EFFECT,
	REACTION_RAN
} from '#client/constants';
import { async_mode_flag } from '../../flags/index.js';
import { deferred, define_property, includes } from '../../shared/utils.js';
import {
	active_effect,
	active_reaction,
	get,
	increment_write_version,
	is_dirty,
	update_effect
} from '../runtime.js';
import * as e from '../errors.js';
import { flush_tasks, queue_micro_task } from '../dom/task.js';
import { DEV } from 'esm-env';
import { invoke_error_boundary } from '../error-handling.js';
import { flush_eager_effects, old_values, set_eager_effects, source, update } from './sources.js';
import { eager_effect, unlink_effect } from './effects.js';
import { defer_effect } from './utils.js';
import { UNINITIALIZED } from '../../../constants.js';
import { set_signal_status } from './status.js';
import { legacy_is_updating_store } from './store.js';
import { invariant } from '../../shared/dev.js';
import { log_effect_tree } from '../dev/debug.js';

/*                     */
const batches = new Set();

/*                       */
export let current_batch = null;

/**
 *
 *
 */
export let previous_batch = null;

/**
 *
 *
 *
 *
 */
export let batch_values = null;

/*                        */
let last_scheduled_effect = null;

export let is_flushing_sync = false;
let is_processing = false;

/**
 *
 *
 *
 *
 */
export let collected_effects = null;

/**
 *
 *
 *
 *
 *
 */
export let legacy_updates = null;

var flush_count = 0;
var source_stacks = DEV ? new Set() : null;

let uid = 1;

export class Batch {
	/*                                              */
	id = uid++;

	/**
 *
 *
 *
 */
	current = new Map();

	/**
 *
 *
 *
 */
	previous = new Map();

	/**
 *
 *
 *
 */
	#commit_callbacks = new Set();

	/**
 *
 *
 */
	#discard_callbacks = new Set();

	/**
 *
 */
	#pending = 0;

	/**
 *
 */
	#blocking_pending = 0;

	/**
 *
 *
 *
 */
	#deferred = null;

	/**
 *
 *
 */
	#roots = [];

	/**
 *
 *
 */
	#dirty_effects = new Set();

	/**
 *
 *
 */
	#maybe_dirty_effects = new Set();

	/**
 *
 *
 *
 *
 *
 */
	#skipped_branches = new Map();

	is_fork = false;

	#decrement_queued = false;

	#is_deferred() {
		return this.is_fork || this.#blocking_pending > 0;
	}

	/**
 *
 *
 */
	skip_effect(effect) {
		if (!this.#skipped_branches.has(effect)) {
			this.#skipped_branches.set(effect, { d: [], m: [] });
		}
	}

	/**
 *
 *
 *
 */
	unskip_effect(effect) {
		var tracked = this.#skipped_branches.get(effect);
		if (tracked) {
			this.#skipped_branches.delete(effect);

			for (var e of tracked.d) {
				set_signal_status(e, DIRTY);
				this.schedule(e);
			}

			for (e of tracked.m) {
				set_signal_status(e, MAYBE_DIRTY);
				this.schedule(e);
			}
		}
	}

	#process() {
		if (flush_count++ > 1000) {
			batches.delete(this);
			infinite_loop_guard();
		}

		/*                                                          */
		/*                                                */
		if (!this.#is_deferred()) {
			for (const e of this.#dirty_effects) {
				this.#maybe_dirty_effects.delete(e);
				set_signal_status(e, DIRTY);
				this.schedule(e);
			}

			for (const e of this.#maybe_dirty_effects) {
				set_signal_status(e, MAYBE_DIRTY);
				this.schedule(e);
			}
		}

		const roots = this.#roots;
		this.#roots = [];

		this.apply();

		/*                   */
		var effects = (collected_effects = []);

		/*                   */
		var render_effects = [];

		/**
 *
 *
 */
		var updates = (legacy_updates = []);

		for (const root of roots) {
			try {
				this.#traverse(root, effects, render_effects);
			} catch (e) {
				reset_all(root);
				throw e;
			}
		}

		/*                                                  */
		current_batch = null;

		if (updates.length > 0) {
			var batch = Batch.ensure();
			for (const e of updates) {
				batch.schedule(e);
			}
		}

		collected_effects = null;
		legacy_updates = null;

		if (this.#is_deferred()) {
			this.#defer_effects(render_effects);
			this.#defer_effects(effects);

			for (const [e, t] of this.#skipped_branches) {
				reset_branch(e, t);
			}
		} else {
			if (this.#pending === 0) {
				batches.delete(this);
			}

			/*                                                                                                      */
			this.#dirty_effects.clear();
			this.#maybe_dirty_effects.clear();

			/*                     */
			for (const fn of this.#commit_callbacks) fn(this);
			this.#commit_callbacks.clear();

			previous_batch = this;
			flush_queued_effects(render_effects);
			flush_queued_effects(effects);
			previous_batch = null;

			this.#deferred?.resolve();
		}

		var next_batch = /*                       */ (/*                  */ (current_batch));

		/*                                                                                                */
		/*                                                                                                  */
		/*                                                                                */
		if (this.#roots.length > 0) {
			const batch = (next_batch ??= this);
			batch.#roots.push(...this.#roots.filter((r) => !batch.#roots.includes(r)));
		}

		if (next_batch !== null) {
			batches.add(next_batch);

			if (DEV) {
				for (const source of this.current.keys()) {
					/*                      */ (source_stacks).add(source);
				}
			}

			next_batch.#process();
		}

		if (!batches.has(this)) {
			this.#commit();
		}
	}

	/**
 *
 *
 *
 *
 *
 */
	#traverse(root, effects, render_effects) {
		root.f ^= CLEAN;

		var effect = root.first;

		while (effect !== null) {
			var flags = effect.f;
			var is_branch = (flags & (BRANCH_EFFECT | ROOT_EFFECT)) !== 0;
			var is_skippable_branch = is_branch && (flags & CLEAN) !== 0;

			var skip = is_skippable_branch || (flags & INERT) !== 0 || this.#skipped_branches.has(effect);

			if (!skip && effect.fn !== null) {
				if (is_branch) {
					effect.f ^= CLEAN;
				} else if ((flags & EFFECT) !== 0) {
					effects.push(effect);
				} else if (async_mode_flag && (flags & (RENDER_EFFECT | MANAGED_EFFECT)) !== 0) {
					render_effects.push(effect);
				} else if (is_dirty(effect)) {
					if ((flags & BLOCK_EFFECT) !== 0) this.#maybe_dirty_effects.add(effect);
					update_effect(effect);
				}

				var child = effect.first;

				if (child !== null) {
					effect = child;
					continue;
				}
			}

			while (effect !== null) {
				var next = effect.next;

				if (next !== null) {
					effect = next;
					break;
				}

				effect = effect.parent;
			}
		}
	}

	/**
 *
 */
	#defer_effects(effects) {
		for (var i = 0; i < effects.length; i += 1) {
			defer_effect(effects[i], this.#dirty_effects, this.#maybe_dirty_effects);
		}
	}

	/**
 *
 *
 *
 *
 */
	capture(source, old_value) {
		if (old_value !== UNINITIALIZED && !this.previous.has(source)) {
			this.previous.set(source, old_value);
		}

		/*                                                                               */
		if ((source.f & ERROR_VALUE) === 0) {
			this.current.set(source, source.v);
			batch_values?.set(source, source.v);
		}
	}

	activate() {
		current_batch = this;
	}

	deactivate() {
		current_batch = null;
		batch_values = null;
	}

	flush() {
		var source_stacks = DEV ? new Set() : null;

		try {
			is_processing = true;
			current_batch = this;

			this.#process();
		} finally {
			flush_count = 0;
			last_scheduled_effect = null;
			collected_effects = null;
			legacy_updates = null;
			is_processing = false;

			current_batch = null;
			batch_values = null;

			old_values.clear();

			if (DEV) {
				for (const source of /*                      */ (source_stacks)) {
					source.updated = null;
				}
			}
		}
	}

	discard() {
		for (const fn of this.#discard_callbacks) fn(this);
		this.#discard_callbacks.clear();

		batches.delete(this);
	}

	#commit() {
		/*                                                                  */
		/*                                                           */
		/*                                                       */
		/*                              */
		for (const batch of batches) {
			var is_earlier = batch.id < this.id;

			/*                   */
			var sources = [];

			for (const [source, value] of this.current) {
				if (batch.current.has(source)) {
					if (is_earlier && value !== batch.current.get(source)) {
						/*                         */
						batch.current.set(source, value);
					} else {
						/*                                               */
						/*                              */
						continue;
					}
				}

				sources.push(source);
			}

			/*                                                                                */
			var others = [...batch.current.keys()].filter((s) => !this.current.has(s));

			if (others.length === 0) {
				if (is_earlier) {
					/*                                              */
					batch.discard();
				}
			} else if (sources.length > 0) {
				if (DEV) {
					invariant(batch.#roots.length === 0, 'Batch has scheduled roots');
				}

				batch.activate();

				/*                     */
				var marked = new Set();

				/*                                 */
				var checked = new Map();

				for (var source of sources) {
					mark_effects(source, others, marked, checked);
				}

				/*                                                                                    */
				if (batch.#roots.length > 0) {
					batch.apply();

					for (var root of batch.#roots) {
						batch.#traverse(root, [], []);
					}

					batch.#roots = [];
				}

				batch.deactivate();
			}
		}
	}

	/**
 *
 *
 */
	increment(blocking) {
		this.#pending += 1;
		if (blocking) this.#blocking_pending += 1;
	}

	/**
 *
 *
 */
	decrement(blocking, skip) {
		this.#pending -= 1;
		if (blocking) this.#blocking_pending -= 1;

		if (this.#decrement_queued || skip) return;
		this.#decrement_queued = true;

		queue_micro_task(() => {
			this.#decrement_queued = false;
			this.flush();
		});
	}

	/**
 *
 *
 */
	transfer_effects(dirty_effects, maybe_dirty_effects) {
		for (const e of dirty_effects) {
			this.#dirty_effects.add(e);
		}

		for (const e of maybe_dirty_effects) {
			this.#maybe_dirty_effects.add(e);
		}

		dirty_effects.clear();
		maybe_dirty_effects.clear();
	}

	/*                                     */
	oncommit(fn) {
		this.#commit_callbacks.add(fn);
	}

	/*                                     */
	ondiscard(fn) {
		this.#discard_callbacks.add(fn);
	}

	settled() {
		return (this.#deferred ??= deferred()).promise;
	}

	static ensure() {
		if (current_batch === null) {
			const batch = (current_batch = new Batch());

			if (!is_processing) {
				batches.add(current_batch);

				if (!is_flushing_sync) {
					queue_micro_task(() => {
						if (current_batch !== batch) {
							/*                                   */
							return;
						}

						batch.flush();
					});
				}
			}
		}

		return current_batch;
	}

	apply() {
		if (!async_mode_flag || (!this.is_fork && batches.size === 1)) {
			batch_values = null;
			return;
		}

		/*                                                        */
		/*                                                        */
		batch_values = new Map(this.current);

		/*                                             */
		for (const batch of batches) {
			if (batch === this || batch.is_fork) continue;

			for (const [source, previous] of batch.previous) {
				if (!batch_values.has(source)) {
					batch_values.set(source, previous);
				}
			}
		}
	}

	/**
 *
 *
 */
	schedule(effect) {
		last_scheduled_effect = effect;

		/*                                             */
		/*                                                                                                          */
		if (
			effect.b?.is_pending &&
			(effect.f & (EFFECT | RENDER_EFFECT | MANAGED_EFFECT)) !== 0 &&
			(effect.f & REACTION_RAN) === 0
		) {
			effect.b.defer_effect(effect);
			return;
		}

		var e = effect;

		while (e.parent !== null) {
			e = e.parent;
			var flags = e.f;

			/*                                                                       */
			/*                                                                  */
			/*                                     */
			if (collected_effects !== null && e === active_effect) {
				if (async_mode_flag) return;

				/*                                                                         */
				/*                                                                           */
				/*                                                                    */
				/*                                                              */
				/*                                                          */
				/*                                */
				if (
					(active_reaction === null || (active_reaction.f & DERIVED) === 0) &&
					!legacy_is_updating_store
				) {
					return;
				}
			}

			if ((flags & (ROOT_EFFECT | BRANCH_EFFECT)) !== 0) {
				if ((flags & CLEAN) === 0) {
					/*                            */
					return;
				}

				e.f ^= CLEAN;
			}
		}

		this.#roots.push(e);
	}
}

/**
 *
 *
 *
 *
 *
 */
export function flushSync(fn) {
	var was_flushing_sync = is_flushing_sync;
	is_flushing_sync = true;

	try {
		var result;

		if (fn) {
			if (current_batch !== null && !current_batch.is_fork) {
				current_batch.flush();
			}

			result = fn();
		}

		while (true) {
			flush_tasks();

			if (current_batch === null) {
				return /*            */ (result);
			}

			current_batch.flush();
		}
	} finally {
		is_flushing_sync = was_flushing_sync;
	}
}

function infinite_loop_guard() {
	if (DEV) {
		var updates = new Map();

		for (const source of /*                */ (current_batch).current.keys()) {
			for (const [stack, update] of source.updated ?? []) {
				var entry = updates.get(stack);

				if (!entry) {
					entry = { error: update.error, count: 0 };
					updates.set(stack, entry);
				}

				entry.count += update.count;
			}
		}

		for (const update of updates.values()) {
			if (update.error) {
				/*                                  */
				console.error(update.error);
			}
		}
	}

	try {
		e.effect_update_depth_exceeded();
	} catch (error) {
		if (DEV) {
			/*                                               */
			define_property(error, 'stack', { value: '' });
		}

		/*                                                       */
		/*                                                      */
		invoke_error_boundary(error, last_scheduled_effect);
	}
}

/*                             */
export let eager_block_effects = null;

/**
 *
 *
 */
function flush_queued_effects(effects) {
	var length = effects.length;
	if (length === 0) return;

	var i = 0;

	while (i < length) {
		var effect = effects[i++];

		if ((effect.f & (DESTROYED | INERT)) === 0 && is_dirty(effect)) {
			eager_block_effects = new Set();

			update_effect(effect);

			/*                                                                            */
			/*                                                                        */
			/*                                                                          */
			/*                                                                   */
			/*                 */
			if (
				effect.deps === null &&
				effect.first === null &&
				effect.nodes === null &&
				effect.teardown === null &&
				effect.ac === null
			) {
				/*                                */
				unlink_effect(effect);
			}

			/*                                                                                              */
			/*                                                                        */
			if (eager_block_effects?.size > 0) {
				old_values.clear();

				for (const e of eager_block_effects) {
					/*                                                  */
					if ((e.f & (DESTROYED | INERT)) !== 0) continue;

					/*                                                                                    */
					/*                   */
					const ordered_effects = [e];
					let ancestor = e.parent;
					while (ancestor !== null) {
						if (eager_block_effects.has(ancestor)) {
							eager_block_effects.delete(ancestor);
							ordered_effects.push(ancestor);
						}
						ancestor = ancestor.parent;
					}

					for (let j = ordered_effects.length - 1; j >= 0; j--) {
						const e = ordered_effects[j];
						/*                                                  */
						if ((e.f & (DESTROYED | INERT)) !== 0) continue;
						update_effect(e);
					}
				}

				eager_block_effects.clear();
			}
		}
	}

	eager_block_effects = null;
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
function mark_effects(value, sources, marked, checked) {
	if (marked.has(value)) return;
	marked.add(value);

	if (value.reactions !== null) {
		for (const reaction of value.reactions) {
			const flags = reaction.f;

			if ((flags & DERIVED) !== 0) {
				mark_effects(/*                  */ (reaction), sources, marked, checked);
			} else if (
				(flags & (ASYNC | BLOCK_EFFECT)) !== 0 &&
				(flags & DIRTY) === 0 &&
				depends_on(reaction, sources, checked)
			) {
				set_signal_status(reaction, DIRTY);
				schedule_effect(/*                 */ (reaction));
			}
		}
	}
}

/**
 *
 *
 *
 *
 *
 */
function mark_eager_effects(value, effects) {
	if (value.reactions === null) return;

	for (const reaction of value.reactions) {
		const flags = reaction.f;

		if ((flags & DERIVED) !== 0) {
			mark_eager_effects(/*                  */ (reaction), effects);
		} else if ((flags & EAGER_EFFECT) !== 0) {
			set_signal_status(reaction, DIRTY);
			effects.add(/*                 */ (reaction));
		}
	}
}

/**
 *
 *
 *
 */
function depends_on(reaction, sources, checked) {
	const depends = checked.get(reaction);
	if (depends !== undefined) return depends;

	if (reaction.deps !== null) {
		for (const dep of reaction.deps) {
			if (includes.call(sources, dep)) {
				return true;
			}

			if ((dep.f & DERIVED) !== 0 && depends_on(/*                  */ (dep), sources, checked)) {
				checked.set(/*                  */ (dep), true);
				return true;
			}
		}
	}

	checked.set(reaction, false);

	return false;
}

/**
 *
 *
 */
export function schedule_effect(effect) {
	/*                */ (current_batch).schedule(effect);
}

/*                           */
let eager_versions = [];

function eager_flush() {
	try {
		flushSync(() => {
			for (const version of eager_versions) {
				update(version);
			}
		});
	} finally {
		eager_versions = [];
	}
}

/**
 *
 *
 *
 *
 */
export function eager(fn) {
	var version = source(0);
	var initial = true;
	var value = /*            */ (undefined);

	get(version);

	eager_effect(() => {
		if (initial) {
			/*                                                  */
			/*                                                    */
			var previous_batch_values = batch_values;

			try {
				batch_values = null;
				value = fn();
			} finally {
				batch_values = previous_batch_values;
			}

			return;
		}

		/*                                                   */
		/*                                                     */
		/*                                            */
		if (eager_versions.length === 0) {
			queue_micro_task(eager_flush);
		}

		eager_versions.push(version);
	});

	initial = false;

	return value;
}

/**
 *
 *
 *
 *
 *
 */
function reset_branch(effect, tracked) {
	/*                                                               */
	if ((effect.f & BRANCH_EFFECT) !== 0 && (effect.f & CLEAN) !== 0) {
		return;
	}

	if ((effect.f & DIRTY) !== 0) {
		tracked.d.push(effect);
	} else if ((effect.f & MAYBE_DIRTY) !== 0) {
		tracked.m.push(effect);
	}

	set_signal_status(effect, CLEAN);

	var e = effect.first;
	while (e !== null) {
		reset_branch(e, tracked);
		e = e.next;
	}
}

/**
 *
 *
 */
function reset_all(effect) {
	set_signal_status(effect, CLEAN);

	var e = effect.first;
	while (e !== null) {
		reset_all(e);
		e = e.next;
	}
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function fork(fn) {
	if (!async_mode_flag) {
		e.experimental_async_required('fork');
	}

	if (current_batch !== null) {
		e.fork_timing();
	}

	var batch = Batch.ensure();
	batch.is_fork = true;
	batch_values = new Map();

	var committed = false;
	var settled = batch.settled();

	flushSync(fn);

	/*                   */
	for (var [source, value] of batch.previous) {
		source.v = value;
	}

	return {
		commit: async () => {
			if (committed) {
				await settled;
				return;
			}

			if (!batches.has(batch)) {
				e.fork_discarded();
			}

			committed = true;

			batch.is_fork = false;

			/*                                                                 */
			for (var [source, value] of batch.current) {
				source.v = value;
				source.wv = increment_write_version();
			}

			/*                                                              */
			/*                                                          */
			/*                                                     */
			/*                     */
			/*                                          */
			flushSync(() => {
				/*                      */
				var eager_effects = new Set();

				for (var source of batch.current.keys()) {
					mark_eager_effects(source, eager_effects);
				}

				set_eager_effects(eager_effects);
				flush_eager_effects();
			});

			batch.flush();
			await settled;
		},
		discard: () => {
			/*                                       */
			/*                                     */
			/*                        */
			for (var source of batch.current.keys()) {
				source.wv = increment_write_version();
			}

			if (!committed && batches.has(batch)) {
				batch.discard();
			}
		}
	};
}

/**
 *
 */
export function clear() {
	batches.clear();
}
